import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Filter, Download, TrendingUp, TrendingDown, BarChart3, RefreshCw, X, ArrowUp, ArrowDown, ChevronUp, ChevronDown, HelpCircle, Info, BookOpen, Lock } from 'lucide-react';
import api from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import StructuredData from '../components/StructuredData';
import CanonicalURL from '../components/CanonicalURL';
import Disclaimer from '../components/Disclaimer';

// Sample data for example/preview mode (when not logged in or demo)
const SAMPLE_BULK_RESULTS = [
  { symbol: 'AAPL', currentPrice: 178.50, priceChange: -1.2, signal: 'BUY', confidence: 72, rsi: 28.5, divergenceScore: 22, extendedOversold: { meetsCriteria: true, daysUnder30: 14, daysUnder20: 7, description: 'Extended oversold' }, cupHandle: { detected: true, confidence: 78 }, sceneOfCrime: { detected: true, confidence: 65, stage: 'Return' }, sentimentScore: 58.2, avgVolume10Day: 52000000, volume: 48100000, riskScore: 4 },
  { symbol: 'MSFT', currentPrice: 415.20, priceChange: 0.8, signal: 'HOLD', confidence: 68, rsi: 45.2, divergenceScore: 38, extendedOversold: { meetsCriteria: false, daysUnder30: 2, daysUnder20: 0 }, cupHandle: { detected: true, confidence: 82 }, sceneOfCrime: { detected: false }, sentimentScore: 68.5, avgVolume10Day: 22500000, volume: 19800000, riskScore: 5 },
  { symbol: 'NVDA', currentPrice: 485.00, priceChange: -2.5, signal: 'BUY', confidence: 75, rsi: 26.8, divergenceScore: 19, extendedOversold: { meetsCriteria: true, daysUnder30: 16, daysUnder20: 8 }, cupHandle: { detected: true, confidence: 71 }, sceneOfCrime: { detected: true, confidence: 58, stage: 'Breakout' }, sentimentScore: 62.1, avgVolume10Day: 45000000, volume: 52000000, riskScore: 6 },
  { symbol: 'GOOGL', currentPrice: 142.80, priceChange: -0.5, signal: 'BUY', confidence: 70, rsi: 31.2, divergenceScore: 28, extendedOversold: { meetsCriteria: false, daysUnder30: 5, daysUnder20: 1 }, cupHandle: { detected: false }, sceneOfCrime: { detected: false }, sentimentScore: 55.3, avgVolume10Day: 28500000, volume: 31200000, riskScore: 4 },
  { symbol: 'META', currentPrice: 498.50, priceChange: 1.2, signal: 'HOLD', confidence: 65, rsi: 52.1, divergenceScore: 45, extendedOversold: { meetsCriteria: false, daysUnder30: 0, daysUnder20: 0 }, cupHandle: { detected: true, confidence: 69 }, sceneOfCrime: { detected: false }, sentimentScore: 71.2, avgVolume10Day: 18500000, volume: 16200000, riskScore: 5 },
  { symbol: 'AMZN', currentPrice: 178.90, priceChange: -1.8, signal: 'BUY', confidence: 73, rsi: 27.8, divergenceScore: 24, extendedOversold: { meetsCriteria: true, daysUnder30: 12, daysUnder20: 5 }, cupHandle: { detected: false }, sceneOfCrime: { detected: true, confidence: 72, stage: 'Return' }, sentimentScore: 59.8, avgVolume10Day: 52000000, volume: 48500000, riskScore: 5 },
  { symbol: 'TSLA', currentPrice: 248.20, priceChange: -3.2, signal: 'SELL', confidence: 62, rsi: 72.5, divergenceScore: 68, extendedOversold: { meetsCriteria: false, daysUnder30: 0, daysUnder20: 0 }, cupHandle: { detected: false }, sceneOfCrime: { detected: false }, sentimentScore: 48.2, avgVolume10Day: 95000000, volume: 112000000, riskScore: 7 },
  { symbol: 'JPM', currentPrice: 198.40, priceChange: 0.3, signal: 'HOLD', confidence: 66, rsi: 48.2, divergenceScore: 42, extendedOversold: { meetsCriteria: false, daysUnder30: 3, daysUnder20: 0 }, cupHandle: { detected: true, confidence: 74 }, sceneOfCrime: { detected: false }, sentimentScore: 64.5, avgVolume10Day: 12500000, volume: 11800000, riskScore: 4 },
];

const SAMPLE_STATS = { total: 8, buy: 4, sell: 1, hold: 3, avgConfidence: 69 };

const BulkStockAnalysis = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isDemo = user?.isDemo === true;
  const isExampleMode = !isAuthenticated || isDemo; // Show example + disabled button when not logged in or demo
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [statusMessage, setStatusMessage] = useState('');
  const [stats, setStats] = useState(null);
  const eventSourceRef = useRef(null);
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const [showCriteria, setShowCriteria] = useState(false); // Show all stocks by default, user can filter
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [quickFilters, setQuickFilters] = useState({
    bestOpportunities: false, // High divergence + Low RSI
    cupHandlePatterns: false,
    sceneOfCrimePatterns: false, // Breakout return patterns
    oversoldStocks: false, // RSI < 30
    highConfidence: false // Confidence > 70
  });
  const [loadingPrevious, setLoadingPrevious] = useState(true);
  const [showHelpToolbar, setShowHelpToolbar] = useState(false);
  
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minVolume: '',
    minConfidence: '',
    signal: 'all',
    highDivergence: false,
    lowRSI: false,
    sceneOfCrime: false,
    cupHandle: false,
    goodSentiment: false,
    goodVolume: false
  });

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError(null);
    setResults([]);
    setFilteredResults([]);
    setProgress({ current: 0, total: 0, percentage: 0 });
    setStatusMessage('Connecting to analysis server...');
    setStats(null);

    // Determine API URL - same logic as apiClient.js
    const getApiUrl = () => {
      if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
      }
      const isLocal = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname === '0.0.0.0');
      return isLocal ? 'http://localhost:3001' : '';
    };
    
    const apiUrl = getApiUrl();
    const endpoint = `${apiUrl}/api/stocks/analyze-bulk`;
    const token = localStorage.getItem('accessToken');

    try {
      // No timeout on initial fetch - SSE streams can run for hours
      // QUIC protocol errors occur when connection times out during long operations
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          usePolygonIPO: true,
          concepts: ['momentum', 'value', 'growth', 'technical']
        })
        // Removed AbortController signal - let SSE stream run without timeout
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to start analysis: ${response.status} ${errorText}`);
      }

      if (!response.body || typeof response.body.getReader !== 'function') {
        throw new Error('Response body is not readable');
      }

      setStatusMessage('Connected. Reading stream...');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let hasReceivedData = false;
      let messageCount = 0;
      

      while (true) {
        try {
          const { done, value } = await reader.read();
          
          if (done) {
            if (!hasReceivedData) {
              setError('No data received from server. Check server logs.');
            }
            break;
          }

          hasReceivedData = true;
          
          const decoded = decoder.decode(value, { stream: true });
          
          buffer += decoded;
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.trim() === '' || line.startsWith(':')) {
              continue; // Skip empty lines and keep-alive comments
            }
            
            
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6).trim();
              if (!jsonStr) {
                continue;
              }
              
              try {
                const data = JSON.parse(jsonStr);
                messageCount++;
                
              if (data.type === 'connected') {
                setStatusMessage('✅ Connected! Fetching stock list...');
              } else if (data.type === 'status') {
                setStatusMessage(data.message);
              } else if (data.type === 'total') {
                setProgress(prev => ({ ...prev, total: data.total }));
                setStatusMessage(`📊 Analyzing ${data.total} stocks...`);
                // Force a re-render to show the progress bar
                setResults(prev => [...prev]);
                } else if (data.type === 'progress') {
                  setProgress({
                    current: data.current,
                    total: data.total,
                    percentage: parseFloat(data.percentage)
                  });
                } else if (data.type === 'result') {
                  // Add result in real-time
                  setResults(prev => {
                    const newResults = [...prev, data.data];
                    calculateStats(newResults);
                    return newResults;
                  });
                } else if (data.type === 'complete') {
                  setStatusMessage('Analysis complete!');
                  setAnalyzing(false);
                } else if (data.type === 'error') {
                  setError(data.message);
                  setAnalyzing(false);
                }
              } catch (parseError) {
                // Silently skip invalid JSON lines
              }
            }
          }
        } catch (readError) {
          setError('Error reading stream: ' + readError.message);
          setAnalyzing(false);
          break;
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to analyze stocks. Please try again.');
      setAnalyzing(false);
    }
  };

  const calculateStats = (data) => {
    if (!data || data.length === 0) return;

    const stats = {
      total: data.length,
      buy: data.filter(r => r.signal === 'BUY').length,
      sell: data.filter(r => r.signal === 'SELL').length,
      hold: data.filter(r => r.signal === 'HOLD').length,
      avgConfidence: data.reduce((sum, r) => sum + (r.confidence || 0), 0) / data.length,
      avgPrice: data.reduce((sum, r) => sum + (r.currentPrice || 0), 0) / data.length,
      highDivergence: data.filter(r => r.meetsCriteria?.highDivergence).length,
      lowRSI: data.filter(r => r.meetsCriteria?.lowRSI).length,
      cupHandle: data.filter(r => r.meetsCriteria?.hasCupHandle).length,
      sceneOfCrime: data.filter(r => r.meetsCriteria?.hasSceneOfCrime).length,
      goodSentiment: data.filter(r => r.meetsCriteria?.goodSentiment).length,
      goodVolume: data.filter(r => r.meetsCriteria?.goodVolume).length
    };
    setStats(stats);
    
    // Update sessionStorage for navigation persistence
    sessionStorage.setItem('bulkAnalysisResults', JSON.stringify(data));
    sessionStorage.setItem('bulkAnalysisStats', JSON.stringify(stats));
  };

  const applyFilters = () => {
    let filtered = [...results];

    // Enhanced search filter - search symbol, price, signal, etc.
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(r => {
        const symbolMatch = r.symbol?.toLowerCase().includes(searchLower);
        const priceMatch = r.currentPrice?.toString().includes(searchLower);
        const signalMatch = r.signal?.toLowerCase().includes(searchLower);
        const rsiMatch = r.rsi?.toString().includes(searchLower);
        const divergenceMatch = r.divergenceScore?.toString().includes(searchLower);
        return symbolMatch || priceMatch || signalMatch || rsiMatch || divergenceMatch;
      });
    }

    // Quick filters
    if (quickFilters.bestOpportunities) {
      filtered = filtered.filter(r => 
        r.meetsCriteria?.highDivergence && r.meetsCriteria?.lowRSI
      );
    }
      if (quickFilters.sceneOfCrimePatterns) {
        filtered = filtered.filter(r => r.meetsCriteria?.hasSceneOfCrime);
      }
    if (quickFilters.cupHandlePatterns) {
      filtered = filtered.filter(r => r.meetsCriteria?.hasCupHandle);
    }
    if (quickFilters.oversoldStocks) {
      filtered = filtered.filter(r => (r.rsi || 50) < 30);
    }
    if (quickFilters.highConfidence) {
      filtered = filtered.filter(r => (r.confidence || 0) > 70);
    }

    // Price filters
    if (filters.minPrice) {
      filtered = filtered.filter(r => r.currentPrice >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(r => r.currentPrice <= parseFloat(filters.maxPrice));
    }

    // Volume filter
    if (filters.minVolume) {
      filtered = filtered.filter(r => (r.avgVolume10Day || 0) >= parseFloat(filters.minVolume));
    }

    // Confidence filter
    if (filters.minConfidence) {
      filtered = filtered.filter(r => (r.confidence || 0) >= parseFloat(filters.minConfidence));
    }

    // Signal filter
    if (filters.signal !== 'all') {
      filtered = filtered.filter(r => r.signal === filters.signal);
    }

      if (filters.sceneOfCrime) {
        filtered = filtered.filter(r => r.meetsCriteria?.hasSceneOfCrime);
      }
    // Criteria filters
    if (filters.highDivergence) {
      filtered = filtered.filter(r => r.meetsCriteria?.highDivergence);
    }
    if (filters.lowRSI) {
      filtered = filtered.filter(r => r.meetsCriteria?.lowRSI);
    }
    if (filters.cupHandle) {
      filtered = filtered.filter(r => r.meetsCriteria?.hasCupHandle);
    }
    if (filters.sceneOfCrime) {
      filtered = filtered.filter(r => r.meetsCriteria?.hasSceneOfCrime);
    }
    if (filters.goodSentiment) {
      filtered = filtered.filter(r => r.meetsCriteria?.goodSentiment);
    }
    if (filters.goodVolume) {
      filtered = filtered.filter(r => r.meetsCriteria?.goodVolume);
    }

    // Show only stocks matching ALL criteria if enabled
    if (showCriteria) {
      filtered = filtered.filter(r => {
        const criteria = r.meetsCriteria || {};
        return criteria.highDivergence && criteria.lowRSI && criteria.hasCupHandle && 
               criteria.goodSentiment && criteria.goodVolume;
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        // Handle nested properties
        if (sortConfig.key === 'divergenceScore') {
          aVal = a.divergenceScore;
          bVal = b.divergenceScore;
        } else if (sortConfig.key === 'cupHandleConfidence') {
          aVal = a.cupHandle?.confidence || 0;
          bVal = b.cupHandle?.confidence || 0;
        } else if (sortConfig.key === 'avgVolume10Day') {
          aVal = a.avgVolume10Day || 0;
          bVal = b.avgVolume10Day || 0;
        }
        
        // Handle null/undefined
        if (aVal == null) aVal = 0;
        if (bVal == null) bVal = 0;
        
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }
    
    setFilteredResults(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Load sample data when in example mode (not logged in or demo)
  useEffect(() => {
    if (isExampleMode) {
      setResults(SAMPLE_BULK_RESULTS);
      setFilteredResults(SAMPLE_BULK_RESULTS);
      setStats(SAMPLE_STATS);
      setLoadingPrevious(false);
    }
  }, [isExampleMode]);

  // Load previous analysis on mount (skip when example mode)
  useEffect(() => {
    if (isExampleMode) return;

    const loadPreviousAnalysis = async () => {
      try {
        setLoadingPrevious(true);
        
        // First check sessionStorage (for navigation back)
        const savedResults = sessionStorage.getItem('bulkAnalysisResults');
        const savedStats = sessionStorage.getItem('bulkAnalysisStats');
        const savedProgress = sessionStorage.getItem('bulkAnalysisProgress');
        
        if (savedResults && savedStats) {
          setResults(JSON.parse(savedResults));
          setStats(JSON.parse(savedStats));
          if (savedProgress) {
            setProgress(JSON.parse(savedProgress));
          }
          setStatusMessage('Restored previous analysis from session');
          setLoadingPrevious(false);
          return;
        }
        
        // Otherwise load from database
        const response = await api.get('/api/stocks/analyze-bulk/latest');
        if (response.data.success && response.data.data) {
          const analysis = response.data.data;
          setResults(analysis.results || []);
          setStats(analysis.stats || null);
          setProgress({
            current: analysis.totalStocks || 0,
            total: analysis.totalStocks || 0,
            percentage: 100
          });
          setStatusMessage(`Loaded previous analysis from ${new Date(analysis.analysisDate).toLocaleString()}`);
          
          // Also save to sessionStorage
          sessionStorage.setItem('bulkAnalysisResults', JSON.stringify(analysis.results || []));
          sessionStorage.setItem('bulkAnalysisStats', JSON.stringify(analysis.stats || {}));
          sessionStorage.setItem('bulkAnalysisProgress', JSON.stringify({
            current: analysis.totalStocks || 0,
            total: analysis.totalStocks || 0,
            percentage: 100
          }));
        }
      } catch (err) {
        // No previous analysis found - that's okay
      } finally {
        setLoadingPrevious(false);
      }
    };

    loadPreviousAnalysis();
  }, [isExampleMode]);

  // Handle stock symbol click - navigate to individual analysis (or login if example mode)
  const handleStockClick = (symbol) => {
    if (isExampleMode) {
      navigate('/login', { state: { from: `/individual-stock-analysis?symbol=${symbol}` } });
      return;
    }
    // Store current analysis state in sessionStorage for when user returns
    sessionStorage.setItem('bulkAnalysisResults', JSON.stringify(results));
    sessionStorage.setItem('bulkAnalysisStats', JSON.stringify(stats));
    sessionStorage.setItem('bulkAnalysisProgress', JSON.stringify(progress));
    
    navigate(`/individual-stock-analysis?symbol=${symbol}`, {
      state: { fromBulkAnalysis: true }
    });
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, results, showCriteria, sortConfig, quickFilters]);

  const exportResults = () => {
    const csv = [
      ['Symbol', 'Price', 'Change%', 'Signal', 'Confidence', 'RSI', 'Divergence', 'Cup&Handle', 'Sentiment', '10D Avg Vol', 'Volume', 'Risk'].join(','),
      ...filteredResults.map(r => [
        r.symbol,
        r.currentPrice?.toFixed(2) || '',
        r.priceChange?.toFixed(2) || '',
        r.signal || '',
        r.confidence || '',
        r.rsi?.toFixed(2) || '',
        r.divergenceScore?.toFixed(2) || '',
        r.cupHandle?.detected ? `${r.cupHandle.confidence}%` : 'No',
        r.sceneOfCrime?.detected ? `${r.sceneOfCrime.confidence}% (${r.sceneOfCrime.stage || 'N/A'})` : 'No',
        r.sentimentScore?.toFixed(1) || '',
        r.avgVolume10Day?.toLocaleString() || '',
        r.volume?.toLocaleString() || '',
        r.riskScore || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `universal-stock-analysis-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <>
      <StructuredData type="application" page="bulk-analysis" />
      <CanonicalURL />
      <div className="min-h-screen bg-gray-50 py-8 px-2 sm:px-4 lg:px-6">
      <style>{`
        .bulk-analysis-table {
          table-layout: fixed !important;
          width: 1610px !important;
          min-width: 1610px !important;
          max-width: 1610px !important;
          border-collapse: collapse !important;
        }
        .bulk-analysis-table colgroup col:nth-child(1) { width: 80px !important; min-width: 80px !important; }
        .bulk-analysis-table colgroup col:nth-child(2) { width: 75px !important; min-width: 75px !important; }
        .bulk-analysis-table colgroup col:nth-child(3) { width: 80px !important; min-width: 80px !important; }
        .bulk-analysis-table colgroup col:nth-child(4) { width: 75px !important; min-width: 75px !important; }
        .bulk-analysis-table colgroup col:nth-child(5) { width: 65px !important; min-width: 65px !important; }
        .bulk-analysis-table colgroup col:nth-child(6) { width: 110px !important; min-width: 110px !important; }
        .bulk-analysis-table colgroup col:nth-child(7) { width: 90px !important; min-width: 90px !important; }
        .bulk-analysis-table colgroup col:nth-child(8) { width: 100px !important; min-width: 100px !important; }
        .bulk-analysis-table colgroup col:nth-child(9) { width: 120px !important; min-width: 120px !important; }
        .bulk-analysis-table colgroup col:nth-child(10) { width: 85px !important; min-width: 85px !important; }
        .bulk-analysis-table colgroup col:nth-child(11) { width: 100px !important; min-width: 100px !important; }
        .bulk-analysis-table colgroup col:nth-child(12) { width: 75px !important; min-width: 75px !important; }
        .bulk-analysis-table colgroup col:nth-child(13) { width: 65px !important; min-width: 65px !important; }
        .bulk-analysis-table th,
        .bulk-analysis-table td {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          padding-left: 8px !important;
          padding-right: 8px !important;
          padding-top: 8px !important;
          padding-bottom: 8px !important;
        }
        .bulk-analysis-table th.px-1.5,
        .bulk-analysis-table td.px-1.5 {
          padding-left: 8px !important;
          padding-right: 8px !important;
        }
        .bulk-analysis-table-wrapper {
          width: 100% !important;
          overflow-x: scroll !important;
          overflow-y: visible !important;
          display: block !important;
          -webkit-overflow-scrolling: touch !important;
        }
        .bulk-analysis-table-wrapper::-webkit-scrollbar {
          height: 12px;
        }
        .bulk-analysis-table-wrapper::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .bulk-analysis-table-wrapper::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 6px;
        }
        .bulk-analysis-table-wrapper::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        .bulk-analysis-table-top-scrollbar {
          width: 100% !important;
          overflow-x: scroll !important;
          overflow-y: hidden !important;
          height: 20px !important;
          margin-bottom: 0 !important;
          border-bottom: 1px solid #e5e7eb !important;
        }
        .bulk-analysis-table-top-scrollbar::-webkit-scrollbar {
          height: 12px;
        }
        .bulk-analysis-table-top-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .bulk-analysis-table-top-scrollbar::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 6px;
        }
        .bulk-analysis-table-top-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        .bulk-analysis-table-top-scrollbar-content {
          height: 1px;
          width: 1610px;
        }
        .bulk-analysis-table-container {
          width: 100% !important;
          overflow-x: auto !important;
          overflow-y: visible !important;
        }
      `}</style>
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Universal Stock Analysis</h1>
            <p className="text-gray-600">Real-time analysis of all available stocks from Polygon.io API</p>
            <Disclaimer variant="investment" />
          </div>
        </div>


        {/* Progress Bar */}
        {analyzing && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            {statusMessage && (
              <div className="mb-3">
                <p className="text-sm text-gray-600">{statusMessage}</p>
              </div>
            )}
            {progress.total > 0 && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Analyzing stocks... {progress.current} / {progress.total}
                  </span>
                  <span className="text-sm font-bold text-blue-600">{progress.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                {progress.current > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {results.length} stocks analyzed so far. Results appear in real-time below.
                  </p>
                )}
              </>
            )}
            {progress.total === 0 && !statusMessage.includes('Error') && (
              <p className="text-sm text-gray-500">Initializing analysis...</p>
            )}
          </div>
        )}

        {/* Example mode banner - not logged in or demo */}
        {isExampleMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6 flex items-start gap-3">
            <Lock className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Create an account to run bulk analysis</h3>
              <p className="text-amber-700 text-sm mt-1">
                This is an example of what the Universal Stock Analysis page looks like. To analyze 5000+ stocks in real time,{' '}
                <Link to="/register" className="underline font-medium hover:text-amber-900">sign up for a free account</Link>.
                {isDemo && ' Demo accounts can try individual stock analysis (up to 2 per session).'}
              </p>
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-3 items-center">
              <button
                onClick={handleAnalyze}
                disabled={analyzing || isExampleMode}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center"
              >
                {analyzing ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                    Analyzing All Stocks...
                  </>
                ) : (
                  <>
                    <BarChart3 className="h-5 w-5 mr-2" />
                    Start Universal Analysis
                  </>
                )}
              </button>
              <button
                onClick={() => setShowHelpToolbar(!showHelpToolbar)}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-colors shadow-sm ${
                  showHelpToolbar 
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-2 border-blue-300'
                }`}
              >
                <HelpCircle className="h-5 w-5" />
                <span>{showHelpToolbar ? 'Hide Help' : 'Show Help Toolbar'}</span>
              </button>
            </div>
            {filteredResults.length > 0 && (
              <button
                onClick={exportResults}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center"
              >
                <Download className="h-5 w-5 mr-2" />
                Export CSV
              </button>
            )}
          </div>
        </div>

        {/* Help Toolbar - Appears directly under the action bar */}
        {showHelpToolbar && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-lg border-2 border-blue-200 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Understanding Stock Analysis Features</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* RSI Extended */}
              <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingDown className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">RSI Extended</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Stocks with RSI under 30 for 12+ consecutive trading days AND under 20 for at least 6 of those days.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Why it matters:</strong> Extended oversold conditions often indicate strong buying opportunities. These stocks have been oversold for an extended period, suggesting potential reversal.
                </p>
                <p className="text-xs text-blue-600 font-medium">
                  💡 Look for: Checkmark (✓) indicates criteria met. Format: "12d/6d" = 12 days under 30, 6 days under 20.
                </p>
              </div>

              {/* Divergence Score */}
              <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Divergence Score</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Lower scores = BETTER buying opportunities. Measures price vs. momentum divergence.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Zones:</strong>
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-2">
                  <li>🔴 <strong>Red Zone (&lt;30):</strong> Excellent buy opportunities</li>
                  <li>🟡 <strong>Yellow Zone (30-50):</strong> Good buy opportunities</li>
                  <li>⚪ <strong>Neutral (50-65):</strong> Watch and wait</li>
                  <li>⚫ <strong>Poor (&gt;65):</strong> Avoid or consider selling</li>
                </ul>
                <p className="text-xs text-purple-600 font-medium">
                  💡 Remember: Lower divergence = Better buy opportunity!
                </p>
              </div>

              {/* Cup & Handle */}
              <div className="bg-white rounded-lg p-4 border border-orange-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Cup & Handle Pattern</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> A bullish reversal pattern showing a "cup" formation followed by a "handle" consolidation.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Why it matters:</strong> This pattern often precedes significant upward price movements. The handle should be bullish for 5+ consecutive days before breakout.
                </p>
                <p className="text-xs text-orange-600 font-medium">
                  💡 Look for: High confidence % indicates pattern strength. "Ready for breakout" = best entry point.
                </p>
              </div>

              {/* Scene of Crime */}
              <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingDown className="h-5 w-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Scene of Crime Pattern</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Stock breaks through major resistance (trendline hit 6+ times over 1+ year), then returns to that level.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Why it matters:</strong> The "scene of crime" (previous resistance) often becomes strong support. Bounce from this level can indicate strong buying opportunity.
                </p>
                <p className="text-xs text-purple-600 font-medium">
                  💡 Look for: Breakout within last 30 trading days, then return to support level.
                </p>
              </div>

              {/* Risk Score */}
              <div className="bg-white rounded-lg p-4 border border-red-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Info className="h-5 w-5 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Risk Score</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> 0-10 scale combining RSI levels, trading signals, and divergence scores.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Ratings:</strong>
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-2">
                  <li>🟢 <strong>Excellent (8-10):</strong> Low risk, high reward</li>
                  <li>🟡 <strong>Good (6.5-7.9):</strong> Moderate risk</li>
                  <li>⚪ <strong>Fair (5-6.4):</strong> Balanced risk/reward</li>
                  <li>🔴 <strong>Poor (&lt;5):</strong> Higher risk</li>
                </ul>
                <p className="text-xs text-red-600 font-medium">
                  💡 Higher score = Lower risk for BUY opportunities.
                </p>
              </div>

              {/* Volume Analysis */}
              <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Volume Analysis</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Shows current volume and 10-day average volume in millions (M).
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Columns:</strong>
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-2">
                  <li>📊 <strong>10D Avg Vol:</strong> 10-day average volume in millions</li>
                  <li>📈 <strong>Volume:</strong> Current trading volume in millions</li>
                </ul>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>How to interpret:</strong> Compare current Volume to 10D Avg Vol. If current volume is significantly higher (1.5x+), it indicates strong interest. Lower volume (&lt;0.5x) suggests weak interest.
                </p>
                <p className="text-xs text-green-600 font-medium">
                  💡 High volume + BUY signal = Strong confirmation of opportunity.
                </p>
              </div>

              {/* Sentiment Score */}
              <div className="bg-white rounded-lg p-4 border border-yellow-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Sentiment Score</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Market sentiment based on news, social media, and analyst opinions.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Scale:</strong> 0-100 scale. 0-30 = Bearish, 30-70 = Neutral, 70-100 = Bullish. Higher scores indicate more bullish sentiment.
                </p>
                <p className="text-xs text-yellow-600 font-medium">
                  💡 Positive sentiment + BUY signal = Strong market confidence.
                </p>
              </div>

              {/* Confidence */}
              <div className="bg-white rounded-lg p-4 border border-indigo-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Info className="h-5 w-5 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Confidence %</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Algorithm's confidence in the trading signal (BUY/SELL/HOLD).
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Interpretation:</strong>
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-2">
                  <li>🟢 <strong>70%+:</strong> High confidence signal</li>
                  <li>🟡 <strong>50-69%:</strong> Moderate confidence</li>
                  <li>🔴 <strong>&lt;50%:</strong> Lower confidence</li>
                </ul>
                <p className="text-xs text-indigo-600 font-medium">
                  💡 Higher confidence = Stronger signal reliability.
                </p>
              </div>

              {/* Trading Signals */}
              <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-gray-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Trading Signals</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  <strong>What it means:</strong> Algorithm's recommendation based on technical analysis.
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Signals:</strong>
                </p>
                <ul className="text-xs text-gray-600 space-y-1 mb-2">
                  <li>🟢 <strong>BUY:</strong> Strong buying opportunity</li>
                  <li>🔴 <strong>SELL:</strong> Consider selling or avoiding</li>
                  <li>⚪ <strong>HOLD:</strong> Wait for clearer signal</li>
                </ul>
                <p className="text-xs text-gray-600 font-medium">
                  💡 Always combine with other metrics (RSI, Divergence, Volume) for best results.
                </p>
              </div>
            </div>

            {/* Quick Tips Section */}
            <div className="mt-6 bg-blue-100 border border-blue-300 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Info className="h-5 w-5" />
                Quick Trading Tips
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-800">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Best Opportunities:</strong> Look for BUY signals with RSI Extended checkmark, Divergence &lt;30 (Red Zone), and high volume.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Pattern Confirmation:</strong> Cup & Handle + Scene of Crime patterns together = Very strong bullish setup.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Risk Management:</strong> Higher Risk Score (8+) = Lower risk. Always use stop-losses and position sizing.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span><strong>Volume Confirmation:</strong> High volume (1.5x+) confirms strong interest and reduces false signals.</span>
                </div>
              </div>
            </div>

            {/* Data Analysis Info */}
            <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                How We Analyze Data
              </h3>
              <div className="text-sm text-indigo-800 space-y-2">
                <p>
                  <strong>Data Source:</strong> All stock data comes from Polygon.io API with 15-minute delayed pricing for comprehensive market coverage.
                </p>
                <p>
                  <strong>Analysis Period:</strong> RSI calculations use 3 months of historical data (63 periods). Divergence analysis uses 6 months of price data.
                </p>
                <p>
                  <strong>Real-Time Updates:</strong> Analysis runs in real-time as stocks are processed. Results appear immediately as each stock is analyzed.
                </p>
                <p>
                  <strong>Pattern Detection:</strong> Cup & Handle and Scene of Crime patterns are detected using advanced technical analysis algorithms analyzing price action, volume, and trendlines.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-600">Total Analyzed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-600">BUY Signals</p>
              <p className="text-2xl font-bold text-green-600">{stats.buy}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-600">High Divergence</p>
              <p className="text-2xl font-bold text-purple-600">{stats.highDivergence}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-600">Cup & Handle</p>
              <p className="text-2xl font-bold text-orange-600">{stats.cupHandle}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-600">Low RSI</p>
              <p className="text-2xl font-bold text-blue-600">{stats.lowRSI}</p>
            </div>
          </div>
        )}

        {/* Quick Filter Buttons */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Filters:</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setQuickFilters({...quickFilters, bestOpportunities: !quickFilters.bestOpportunities})}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quickFilters.bestOpportunities 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🎯 Best Opportunities (High Div + Low RSI)
              </button>
              <button
                onClick={() => setQuickFilters({...quickFilters, cupHandlePatterns: !quickFilters.cupHandlePatterns})}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quickFilters.cupHandlePatterns 
                    ? 'bg-orange-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ☕ Cup & Handle Patterns
              </button>
              <button
                onClick={() => setQuickFilters({...quickFilters, sceneOfCrimePatterns: !quickFilters.sceneOfCrimePatterns})}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quickFilters.sceneOfCrimePatterns 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🏃 Scene of Crime Patterns
              </button>
              <button
                onClick={() => setQuickFilters({...quickFilters, oversoldStocks: !quickFilters.oversoldStocks})}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quickFilters.oversoldStocks 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📉 Oversold (RSI &lt; 30)
              </button>
              <button
                onClick={() => setQuickFilters({...quickFilters, highConfidence: !quickFilters.highConfidence})}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quickFilters.highConfidence 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                ⭐ High Confidence (&gt;70%)
              </button>
              {(quickFilters.bestOpportunities || quickFilters.cupHandlePatterns || quickFilters.oversoldStocks || quickFilters.highConfidence) && (
                <button
                  onClick={() => setQuickFilters({ bestOpportunities: false, cupHandlePatterns: false, sceneOfCrimePatterns: false, oversoldStocks: false, highConfidence: false })}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}

        {/* Criteria Filter Toggle */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showCriteria}
              onChange={(e) => setShowCriteria(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700">
              Show only stocks matching ALL criteria (High Divergence + Low RSI + Cup & Handle + Scene of Crime + Good Sentiment + Good Volume)
            </span>
          </label>
        </div>

        {/* Filters */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search (Symbol, Price, Signal, RSI, etc.)</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by symbol, price, signal, RSI..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Price</label>
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Price</label>
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
                  placeholder="$1000.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min 10D Avg Vol</label>
                <input
                  type="number"
                  value={filters.minVolume}
                  onChange={(e) => setFilters({...filters, minVolume: e.target.value})}
                  placeholder="1M"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Confidence</label>
                <input
                  type="number"
                  value={filters.minConfidence}
                  onChange={(e) => setFilters({...filters, minConfidence: e.target.value})}
                  placeholder="50%"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Signal</label>
                <select
                  value={filters.signal}
                  onChange={(e) => setFilters({...filters, signal: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Signals</option>
                  <option value="BUY">BUY Only</option>
                  <option value="SELL">SELL Only</option>
                  <option value="HOLD">HOLD Only</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.highDivergence}
                  onChange={(e) => setFilters({...filters, highDivergence: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">High Divergence</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.lowRSI}
                  onChange={(e) => setFilters({...filters, lowRSI: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Low RSI (&lt;30)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.cupHandle}
                  onChange={(e) => setFilters({...filters, cupHandle: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Cup & Handle</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={filters.sceneOfCrime}
                  onChange={(e) => setFilters({...filters, sceneOfCrime: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Scene of Crime</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.goodSentiment}
                  onChange={(e) => setFilters({...filters, goodSentiment: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Good Sentiment</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.goodVolume}
                  onChange={(e) => setFilters({...filters, goodVolume: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600"
                />
                <span className="text-sm text-gray-700">Good Volume</span>
              </label>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Showing {filteredResults.length} of {results.length} results
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <span className="text-red-800">{error}</span>
            <button onClick={() => setError(null)}>
              <X className="h-5 w-5 text-red-600" />
            </button>
          </div>
        )}

        {/* Loading Previous Analysis */}
        {loadingPrevious && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <RefreshCw className="animate-spin h-5 w-5 text-blue-600 mr-2" />
              <span className="text-blue-800">Loading previous analysis...</span>
            </div>
          </div>
        )}

        {/* Results Table - Show even while analyzing */}
        {(filteredResults.length > 0 || (analyzing && results.length > 0)) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200" style={{ overflow: 'visible', width: '100%' }}>
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Analysis Results {analyzing && <span className="text-sm font-normal text-gray-500">(Live - {results.length} stocks analyzed so far)</span>}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Click any stock symbol to view detailed analysis
                  </p>
                </div>
                {filteredResults.length !== results.length && (
                  <span className="text-sm text-gray-600">
                    Showing {filteredResults.length} of {results.length} results
                  </span>
                )}
              </div>
            </div>
            {/* Top Scrollbar */}
            <div 
              className="bulk-analysis-table-top-scrollbar"
              ref={topScrollRef}
              onScroll={(e) => {
                if (bottomScrollRef.current && e.target.scrollLeft !== bottomScrollRef.current.scrollLeft) {
                  bottomScrollRef.current.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <div className="bulk-analysis-table-top-scrollbar-content"></div>
            </div>
            {/* Table with Bottom Scrollbar */}
            <div 
              className="bulk-analysis-table-wrapper" 
              ref={bottomScrollRef}
              style={{ width: '100%', overflowX: 'scroll', overflowY: 'visible', display: 'block' }}
              onScroll={(e) => {
                if (topScrollRef.current && e.target.scrollLeft !== topScrollRef.current.scrollLeft) {
                  topScrollRef.current.scrollLeft = e.target.scrollLeft;
                }
              }}
            >
              <table className="bulk-analysis-table divide-y divide-gray-200" style={{ width: '1610px', minWidth: '1610px', maxWidth: 'none', tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('symbol')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Symbol</span>
                        {sortConfig.key === 'symbol' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('currentPrice')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Price</span>
                        {sortConfig.key === 'currentPrice' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('priceChange')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Change%</span>
                        {sortConfig.key === 'priceChange' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('signal')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Signal</span>
                        {sortConfig.key === 'signal' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('rsi')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>RSI</span>
                        {sortConfig.key === 'rsi' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('extendedOversold')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>RSI Extended</span>
                        {sortConfig.key === 'extendedOversold' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('divergenceScore')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Div</span>
                        {sortConfig.key === 'divergenceScore' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('cupHandleConfidence')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>C&H</span>
                        {sortConfig.key === 'cupHandleConfidence' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('sceneOfCrimeConfidence')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Scene of Crime</span>
                        {sortConfig.key === 'sceneOfCrimeConfidence' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('sentimentScore')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Sentiment</span>
                        {sortConfig.key === 'sentimentScore' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => handleSort('avgVolume10Day')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>10D Avg Vol</span>
                        {sortConfig.key === 'avgVolume10Day' && (
                          sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Volume</th>
                    <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Risk</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredResults.map((result, index) => (
                    <tr key={`${result.symbol}-${index}`} className="hover:bg-gray-50">
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs">
                        <button
                          onClick={() => handleStockClick(result.symbol)}
                          className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                          {result.symbol}
                        </button>
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs text-gray-900">${result.currentPrice?.toFixed(2) || 'N/A'}</td>
                      <td className={`px-1.5 py-2 whitespace-nowrap text-xs ${result.priceChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {result.priceChange?.toFixed(2) || 'N/A'}%
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap">
                        <span className={`px-1 py-0.5 text-xs font-medium rounded-full ${
                          result.signal === 'BUY' ? 'bg-green-100 text-green-800' :
                          result.signal === 'SELL' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {result.signal || 'N/A'}
                        </span>
                      </td>
                      <td className={`px-1.5 py-2 whitespace-nowrap text-xs font-medium ${
                        result.rsi < 30 ? 'text-green-600' : result.rsi > 70 ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {result.rsi?.toFixed(1) || 'N/A'}
                      </td>
                      <td className={`px-1.5 py-2 whitespace-nowrap text-xs ${
                        result.extendedOversold?.meetsCriteria ? 'text-green-600 font-bold' : 'text-gray-500'
                      }`}>
                        {result.extendedOversold?.meetsCriteria ? (
                          <span title={result.extendedOversold.description}>
                            ✓ {result.extendedOversold.daysUnder30}d/{result.extendedOversold.daysUnder20}d
                          </span>
                        ) : (
                          <span className="text-gray-400" title={result.extendedOversold?.description || 'N/A'}>
                            {result.extendedOversold?.daysUnder30 || 0}d/{result.extendedOversold?.daysUnder20 || 0}d
                          </span>
                        )}
                      </td>
                      <td className={`px-1.5 py-2 whitespace-nowrap text-xs font-medium ${
                        result.divergenceScore < 30 ? 'text-purple-600 font-bold' : 'text-gray-900'
                      }`}>
                        {result.divergenceScore?.toFixed(1) || 'N/A'}
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs text-gray-900">
                        {result.cupHandle?.detected ? (
                          <span className="text-orange-600 font-medium">
                            {result.cupHandle.confidence}%
                          </span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs">
                        {result.sceneOfCrime?.detected ? (
                          <div>
                            <span className="text-purple-600 font-medium text-xs">
                              {result.sceneOfCrime.confidence}%
                            </span>
                            <br />
                            <span className="text-xs text-gray-500">{result.sceneOfCrime.stage || 'N/A'}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No</span>
                        )}
                      </td>
                      <td className={`px-1.5 py-2 whitespace-nowrap text-xs font-medium ${
                        result.sentimentScore > 60 ? 'text-green-600' : result.sentimentScore < 40 ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {result.sentimentScore?.toFixed(1) || 'N/A'}
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs text-gray-900">
                        {result.avgVolume10Day ? (result.avgVolume10Day / 1000000).toFixed(1) + 'M' : 'N/A'}
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs text-gray-900">
                        {result.volume ? (result.volume / 1000000).toFixed(1) + 'M' : 'N/A'}
                      </td>
                      <td className="px-1.5 py-2 whitespace-nowrap text-xs text-gray-900 font-medium">{result.riskScore || 'N/A'}/10</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!analyzing && results.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Analyze</h3>
            <p className="text-gray-600 mb-4">Click "Start Universal Analysis" to analyze all available stocks from Polygon.io API</p>
            <p className="text-sm text-gray-500">Results will appear in real-time as they are analyzed</p>
          </div>
        )}

        {/* Show message if analyzing but no results yet */}
        {analyzing && results.length === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <RefreshCw className="h-8 w-8 text-blue-600 mx-auto mb-3 animate-spin" />
            <p className="text-blue-800 font-medium">Fetching stock list and starting analysis...</p>
            <p className="text-blue-600 text-sm mt-2">Results will appear here as each stock is analyzed</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default BulkStockAnalysis;
